import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Shield, 
  FileText, 
  AlertTriangle,
  Phone,
  BookOpen,
  Users,
  Scale,
  DollarSign,
  Clock,
  MapPin,
  ExternalLink
} from 'lucide-react';

interface ClockItRightsProps {
  onNavigateToDisputeAssistance?: () => void;
}

export function ClockItRights({ onNavigateToDisputeAssistance }: ClockItRightsProps = {}) {
  const urgentRights = [
    {
      id: 1,
      title: "Minimum Wage Tracking",
      description: "Track if you're paid at least $15/hour",
      status: "tracked",
      action: "Check Rate"
    },
    {
      id: 2,
      title: "Overtime Pay",
      description: "1.5x pay for hours over 40/week",
      status: "alert",
      action: "Calculate"
    },
    {
      id: 3,
      title: "Break Requirements",
      description: "30min unpaid break for 6+ hour shifts",
      status: "tracked",
      action: "Learn More"
    }
  ];

  const knowledgeAreas = [
    {
      icon: DollarSign,
      title: "Fair Pay",
      description: "Ensuring you get what you've earned",
      gradient: "gradient-sunset",
      topics: 12
    },
    {
      icon: Clock,
      title: "Work-Life Balance",
      description: "Your time matters - breaks and boundaries",
      gradient: "gradient-earth",
      topics: 8
    },
    {
      icon: Shield,
      title: "Stay Safe",
      description: "Protecting your health and wellbeing",
      gradient: "gradient-green",
      topics: 15
    },
    {
      icon: Users,
      title: "Equal Treatment",
      description: "Respect and fairness for everyone",
      gradient: "gradient-blue",
      topics: 10
    }
  ];

  const resources = [
    {
      title: "Worker Rights Hotline",
      description: "24/7 support for workplace issues",
      icon: Phone,
      action: "Call Now",
      urgent: true
    },
    {
      title: "Legal Aid Directory",
      description: "Free legal assistance in your area",
      icon: Scale,
      action: "Find Help",
      urgent: false
    },
    {
      title: "Union Information",
      description: "Learn about organizing and joining unions",
      icon: Users,
      action: "Explore",
      urgent: false
    }
  ];

  return (
    <div className="pb-20 bg-background min-h-screen">
      {/* Header */}
      <div className="px-4 pt-6 pb-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Know Your Rights</h1>
          <p className="text-muted-foreground">
            Empowering you with knowledge to protect your workplace rights
          </p>
        </div>
      </div>

      {/* Urgent Rights Monitoring */}
      <div className="px-4 mb-6">
        <div className="clock-it-card card-content">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Your Rights Dashboard</h2>
            <Badge className="bg-primary/10 text-primary border-primary/20">Live Tracking</Badge>
          </div>
          
          <div className="space-y-3">
            {urgentRights.map((right) => (
              <div key={right.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${
                    right.status === 'tracked' ? 'bg-success' : 'bg-warning animate-pulse'
                  }`}></div>
                  <div>
                    <div className="font-medium text-sm">{right.title}</div>
                    <div className="text-xs text-muted-foreground">{right.description}</div>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="text-primary hover:text-primary/80"
                >
                  {right.action}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Knowledge Areas */}
      <div className="px-4 mb-6">
        <h2 className="font-semibold mb-4">Learn & Grow</h2>
        <div className="clock-it-grid clock-it-grid-2">
          {knowledgeAreas.map((area, index) => {
            const Icon = area.icon;
            return (
              <div key={index} className={`clock-it-card card-content text-white ${area.gradient}`}>
                <Icon className="w-6 h-6 mb-3" />
                <h3 className="font-semibold mb-1">{area.title}</h3>
                <p className="text-sm opacity-90 mb-3">{area.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs opacity-75">{area.topics} topics</span>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="bg-white/20 text-white hover:bg-white/30 h-auto p-1.5"
                  >
                    <BookOpen className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dispute Assistance */}
      {onNavigateToDisputeAssistance && (
        <div className="px-4 mb-6">
          <div className="clock-it-card card-content border-2 border-warning bg-warning/5">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-warning rounded-full flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-warning mb-2">Need Help with a Workplace Issue?</h3>
                <p className="text-sm text-foreground mb-3">
                  Our dispute assistance tool helps you organize documentation and 
                  connect with resources to resolve workplace problems.
                </p>
                <Button 
                  onClick={onNavigateToDisputeAssistance}
                  size="sm"
                  className="bg-warning hover:bg-warning/90 text-white"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Get Dispute Help
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Emergency Resources */}
      <div className="px-4 mb-6">
        <h2 className="font-semibold mb-4">Emergency Resources</h2>
        <div className="space-y-3">
          {resources.map((resource, index) => {
            const Icon = resource.icon;
            return (
              <div key={index} className={`clock-it-card card-content ${
                resource.urgent ? 'border-2 border-destructive bg-destructive/5' : ''
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      resource.urgent ? 'bg-destructive' : 'bg-primary'
                    }`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-medium">{resource.title}</h3>
                      <p className="text-sm text-muted-foreground">{resource.description}</p>
                    </div>
                  </div>
                  <Button 
                    variant={resource.urgent ? "destructive" : "default"}
                    size="sm"
                    className="flex-shrink-0"
                  >
                    {resource.action}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Quick Facts */}
      <div className="px-4 mb-6">
        <div className="clock-it-card card-content">
          <h2 className="font-semibold mb-4">Quick Facts</h2>
          <div className="space-y-4">
            <div className="p-3 bg-success/10 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <DollarSign className="w-4 h-4 text-success" />
                <span className="font-medium text-success">Fair Pay</span>
              </div>
              <p className="text-sm">
                You have the right to be paid for all hours worked, including overtime at 1.5x rate after 40 hours/week.
              </p>
            </div>
            
            <div className="p-3 bg-primary/10 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Clock className="w-4 h-4 text-primary" />
                <span className="font-medium text-primary">Break Time</span>
              </div>
              <p className="text-sm">
                Most states require a 30-minute unpaid break for shifts over 6 hours. Check your local laws.
              </p>
            </div>
            
            <div className="p-3 bg-warning/10 rounded-lg">
              <div className="flex items-center space-x-2 mb-1">
                <Shield className="w-4 h-4 text-warning" />
                <span className="font-medium text-warning">Safe Workplace</span>
              </div>
              <p className="text-sm">
                You have the right to a safe workplace free from hazards and discrimination.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Educational Progress */}
      <div className="px-4 pb-6">
        <div className="clock-it-card card-content">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold">Your Progress</h2>
              <p className="text-xs text-muted-foreground">Knowledge is power</p>
            </div>
            <Badge className="bg-primary/10 text-primary border-primary/20">
              75% Complete
            </Badge>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm">Rights modules completed</span>
              <span className="text-sm font-medium">12/16</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div className="bg-primary rounded-full h-2" style={{ width: '75%' }}></div>
            </div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Started learning</span>
              <span>Expert level</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}